import { Link, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { CONSISTENCY_RULES, FACT_KEYS, SEVERITIES, SEVERITY_GLYPHS, SEVERITY_LABELS, fingerprint, type Finding, type Severity } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { realmFile } from '../db/protocol.ts';

/**
 * The Consistency Check (docs/PARITY-STEP-5-PLAN.md section 6), as the desktop's: what the
 * rules find in the realm, most serious first, filtered by kind and by entry or text; the
 * entries each names, to open; Dismiss... with a note for what's intentional, and the
 * dismissed ones to restore; and What's checked. It runs in the realm's worker when the
 * page opens and on Check again (decision 6), not as you work.
 */

const route = getRouteApi('/_app/realm/$realmKey/consistency');
const realmRoute = getRouteApi('/_app/realm/$realmKey');

const message = (e: unknown, fallback: string) => (e instanceof RealmClientError ? e.message : fallback);

/** The filter buttons' words: "Contradictions", "Needs review", "Suggestion". */
function plural(severity: Severity, n: number): string {
  if (severity === 'WARNING') return 'Needs review';
  return n === 1 ? SEVERITY_LABELS[severity] : `${SEVERITY_LABELS[severity]}s`;
}

export function ConsistencyPage() {
  const report = route.useLoaderData();
  const { realmKey } = route.useParams();
  const { entries } = realmRoute.useLoaderData();
  const file = realmFile(realmKey);
  const router = useRouter();
  const [shown, setShown] = useState<ReadonlySet<Severity>>(new Set(SEVERITIES));
  const [query, setQuery] = useState('');
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const titles = useMemo(() => new Map(entries.map((e) => [e.id, e.title])), [entries]);

  async function run(work: () => Promise<unknown>, fallback: string) {
    setError('');
    try {
      await work();
      setDismissing(null);
      await router.invalidate();
    } catch (e) {
      setError(message(e, fallback));
    }
  }

  async function checkAgain() {
    setChecking(true);
    await run(async () => undefined, 'The realm could not be checked.');
    setChecking(false);
  }

  const q = query.trim().toLowerCase();
  const matches = (f: Finding) =>
    q === '' ||
    f.title.toLowerCase().includes(q) ||
    f.message.toLowerCase().includes(q) ||
    f.entryIds
      .flatMap((id) => titles.get(id) ?? [])
      .map((t) => t.toLowerCase())
      .join(' ')
      .includes(q);
  const visible = report.active.filter((f) => shown.has(f.severity) && matches(f));
  const countOf = (severity: Severity) => report.active.filter((f) => f.severity === severity).length;
  const checkedAt = new Date(report.checkedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const card = (f: Finding, dismissed?: { note: string }) => {
    const id = fingerprint(f);
    return (
      <article key={id} className={`consistency-card consistency-${f.severity.toLowerCase()}${dismissed ? ' consistency-card-dismissed' : ''}`} aria-label={f.title}>
        <header>
          <span aria-hidden="true">{SEVERITY_GLYPHS[f.severity]}</span>
          <strong>{f.title}</strong>
          <span className="chip">{SEVERITY_LABELS[f.severity]}</span>
          {dismissed ? (
            <button type="button" className="quiet" title="Bring this finding back into the list" onClick={() => void run(() => realms.restoreFinding(file, id), 'The finding could not be restored.')}>
              Restore
            </button>
          ) : (
            <button type="button" className="quiet" title="It's intentional: hide it, with a note saying why if you like" onClick={() => setDismissing(dismissing === id ? null : id)}>
              Dismiss…
            </button>
          )}
        </header>
        <p>{f.message}</p>
        {f.entryIds.length > 0 && (
          <p className="chips">
            {[...new Set(f.entryIds)].map((entryId) =>
              titles.has(entryId) ? (
                <Link key={entryId} className="chip" to="/realm/$realmKey/entry/$entryId" params={{ realmKey, entryId }} search={{}}>
                  {titles.get(entryId)} →
                </Link>
              ) : (
                <span key={entryId} className="chip">
                  (missing entry)
                </span>
              ),
            )}
          </p>
        )}
        {dismissed && dismissed.note.trim() !== '' && <p className="meta">Dismissed: {dismissed.note}</p>}
        {dismissing === id && <DismissForm finding={f} onCancel={() => setDismissing(null)} onDismiss={(note) => run(() => realms.dismissFinding(file, f, note), 'The finding could not be dismissed.')} />}
      </article>
    );
  };

  let current: Severity | null = null;
  return (
    <article className="document consistency-page">
      <p className="eyebrow">Consistency Check</p>
      <h1>Consistency Check</h1>
      <p className="lead">Contradictions and loose ends in the realm: dates that can't both be true, relationships that disagree, hierarchies that loop.</p>
      <div className="actions">
        <button type="button" className="primary" disabled={checking} title="Run every check over the realm again now" onClick={() => void checkAgain()}>
          ↻ Check again
        </button>
        <span className="meta" role="status">
          {checking ? 'Checking…' : `Checked ${checkedAt} · ${CONSISTENCY_RULES.length} rules`}
        </span>
      </div>
      <div className="consistency-filters" role="group" aria-label="Show">
        {SEVERITIES.map((severity) => (
          <button
            key={severity}
            type="button"
            className={`quiet consistency-${severity.toLowerCase()}`}
            aria-pressed={shown.has(severity)}
            title="Show or hide the findings of this kind"
            onClick={() => {
              const next = new Set(shown);
              if (!next.delete(severity)) next.add(severity);
              setShown(next);
            }}
          >
            {SEVERITY_GLYPHS[severity]} {countOf(severity)} {plural(severity, countOf(severity))}
          </button>
        ))}
        <label>
          <span className="visually-hidden">Filter by entry or text</span>
          <input type="search" value={query} placeholder="Filter by entry or text" onChange={(e) => setQuery(e.currentTarget.value)} />
        </label>
      </div>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}

      {report.active.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-glyph" aria-hidden="true">
            ✓
          </p>
          <h2>No problems found</h2>
          <p className="meta">
            The more facts entries record, the more the engine can check: add "born" and "died" to people, "founded" to places and factions, and "population" and "food
            production" to settlements in each entry's fields, and link entries with relationships like "child of", "located in", "allied with", and "enemy of".
          </p>
        </div>
      ) : (
        visible.length === 0 && <p className="meta">Nothing matches the current filters.</p>
      )}
      {visible.map((f) => {
        const heading =
          f.severity !== current ? (
            <h2 key={`h-${f.severity}`} className="consistency-group-heading">
              {SEVERITY_GLYPHS[f.severity]} {SEVERITY_LABELS[f.severity]}
            </h2>
          ) : null;
        current = f.severity;
        return [heading, card(f)];
      })}

      {report.dismissed.length > 0 && (
        <details className="consistency-section">
          <summary>Dismissed ({report.dismissed.length})</summary>
          {report.dismissed.map((d) => card(d.finding, d))}
        </details>
      )}
      <details className="consistency-section">
        <summary>What's checked ({CONSISTENCY_RULES.length} rules)</summary>
        <p>Facts read from entry fields (names are matched ignoring case, spaces, and punctuation):</p>
        <ul>
          {Object.entries(FACT_KEYS).map(([label, keys]) => (
            <li key={label}>
              {label}: {keys.join(', ')}
            </li>
          ))}
        </ul>
        <p>Years can be written "402", "Year 402", "402 AE", or "30 BCE"; numbers "45000", "45,000", "45k", or "1.2 million".</p>
        <dl className="consistency-rules">
          {CONSISTENCY_RULES.map((rule) => (
            <div key={rule.id}>
              <dt>{rule.name}</dt>
              <dd className="meta">{rule.description}</dd>
            </div>
          ))}
        </dl>
      </details>
    </article>
  );
}

function DismissForm({ finding, onDismiss, onCancel }: { finding: Finding; onDismiss: (note: string) => Promise<void>; onCancel: () => void }) {
  const [note, setNote] = useState('');
  return (
    <form
      className="panel"
      aria-label={`Dismiss ${finding.title}`}
      onSubmit={(e) => {
        e.preventDefault();
        void onDismiss(note);
      }}
    >
      <p>“{finding.title}” won't be reported again until you restore it. Why is it fine? (Optional; shown with the dismissed finding.)</p>
      <label>
        Note
        <input value={note} maxLength={1000} autoFocus placeholder="e.g. Elena is adopted" onChange={(e) => setNote(e.currentTarget.value)} />
      </label>
      <div className="actions">
        <button type="submit" className="primary">
          Dismiss
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
